import { NavLink, useLocation } from 'react-router-dom'

import { NAV_GROUPS, navItemHref } from '@/components/layout/navItems'
import { Alert } from '@/components/ui/Alert'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { OperationVerificationSummaryCard } from '@/components/venue/OperationVerificationSummaryCard'
import { useVenueProfile } from '@/hooks/useVenueProfile'
import type { VenueProfileView } from '@/lib/venue/venueProfileTypes'
import { toVenueProfileSummaryViewModel } from '@/lib/venue/venueProfileViewModel'
import { useVenueContext } from '@/routes/venueContext'

interface SummaryFact {
  label: string
  value: string
}

interface EntryCardSpec {
  key: string
  label: string
  description: string
  /** `navItems.ts` 에 등록된 항목이면 그 경로의 `status` 를 활성/비활성 판정에 쓴다(설계 D2). */
  navPath: string
  /** 실제 이동 경로. `venue/edit` 처럼 `navItems` 에 없는(사이드바 미노출) 하위 액션 라우트일 수 있다. */
  targetPath: string
}

const ENTRY_CARDS: EntryCardSpec[] = [
  {
    key: 'edit',
    label: '기본정보 편집',
    description: '구장 이름·연락처·시설·운영 안내를 수정합니다.',
    navPath: 'venue',
    targetPath: 'venue/edit',
  },
  {
    key: 'courts',
    label: '코트 목록 관리',
    description: '코트별 정보와 요금을 관리합니다.',
    navPath: 'courts',
    targetPath: 'courts',
  },
  {
    key: 'closures',
    label: '휴무일·차단시간 설정',
    description: '휴무일과 예약 차단 시간을 설정합니다.',
    navPath: 'closures',
    targetPath: 'closures',
  },
  // 🚨 #772 — `navItems` 미등록 라우트다(설계 §2 자율 결정 2). `navPath` 를 등록되지 않은 값으로 두면
  // `findNavItem` 이 `undefined` 를 돌려줘 `isPlanned=false`(항상 활성)가 된다 — `venue/edit` 과 동일 동작.
  {
    key: 'verification',
    label: '운영 인증 신청',
    description: '사업자 정보를 입력해 운영 인증을 신청합니다.',
    navPath: 'operation-verification',
    targetPath: 'operation-verification',
  },
]

function findNavItem(path: string) {
  return NAV_GROUPS.flatMap((group) => group.items).find((item) => item.path === path)
}

/**
 * 구장 정보·코트 설정 요약 (요구사항 1, 설계 §2-1(c)).
 *
 * 🚨 이 화면은 조립만 한다. 값 매핑은 `venueProfileViewModel.ts` 가 소유한다.
 */
export function VenueProfileSummaryPage() {
  const { venueId } = useVenueContext()
  const profileQuery = useVenueProfile(venueId)
  const location = useLocation()
  // 🚨 편집 화면 저장 성공 시 이 state 로 1회성 완료 안내를 띄운다(설계 §2 "저장 완료 안내").
  // 새로고침·직접 진입은 history state 가 없으므로 배너가 뜨지 않는다(재방문마다 계속 뜨지 않는다).
  const profileSaved = (location.state as { profileSaved?: boolean } | null)?.profileSaved === true

  return (
    <>
      <PageHeader
        title="구장 정보·코트 설정"
        description="구장 기본정보와 코트 운영 설정을 한눈에 확인하고 각 설정으로 이동합니다."
      />

      {profileSaved ? (
        <div className="mb-5">
          <Alert tone="info">구장 정보가 저장되었습니다.</Alert>
        </div>
      ) : null}

      {profileQuery.isLoading ? <LoadingState rows={3} label="구장 정보를 불러오는 중" /> : null}

      {profileQuery.isError ? (
        <ErrorState
          error={profileQuery.error}
          onRetry={() => {
            void profileQuery.refetch()
          }}
        />
      ) : null}

      {profileQuery.data ? (
        <div className="space-y-5">
          <Card>
            <CardHeader title={profileQuery.data.basic.name} description={profileQuery.data.location.address} />
            <CardBody>
              <SummaryFactGrid view={profileQuery.data} />
            </CardBody>
          </Card>

          {/* 🚨 #778 요구 1 — 구장 정보 화면에도 인증 상태 배지·요약 카드를 보여준다(대시보드와 공용). */}
          <OperationVerificationSummaryCard venueId={venueId} />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {ENTRY_CARDS.map((card) => (
              <EntryCard key={card.key} venueId={venueId} card={card} />
            ))}
          </div>
        </div>
      ) : null}
    </>
  )
}

function SummaryFactGrid({ view }: { view: VenueProfileView }) {
  const vm = toVenueProfileSummaryViewModel(view)
  const facts: SummaryFact[] = [
    { label: '등록 코트 수', value: vm.courtCountText },
    { label: '운영시간', value: vm.operatingHoursText },
    { label: '예약 단위', value: vm.reservationUnitText },
    { label: '최대 예약 시간', value: vm.maxReservationDurationText },
    { label: '다음 휴무일', value: vm.nextClosureDateText },
    { label: '차단 시간', value: vm.blockedTimeText },
  ]

  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt className="text-xs text-slateish-500">{fact.label}</dt>
          <dd className="mt-0.5 text-sm font-medium text-ink-700">{fact.value}</dd>
        </div>
      ))}
    </dl>
  )
}

function EntryCard({ venueId, card }: { venueId: string; card: EntryCardSpec }) {
  const navItem = findNavItem(card.navPath)
  const isPlanned = navItem === undefined ? false : navItem.status === 'planned'

  if (isPlanned) {
    return (
      <span
        aria-disabled="true"
        className="flex cursor-not-allowed flex-col gap-1 rounded-xl border border-slateish-200 bg-white px-4 py-3.5 text-left shadow-sm"
      >
        <span className="flex items-center justify-between text-sm font-semibold text-slateish-400">
          {card.label}
          <span className="rounded-full bg-slateish-200 px-2 py-0.5 text-[11px] text-slateish-600">준비 중</span>
        </span>
        <span className="text-xs text-slateish-400">{card.description}</span>
      </span>
    )
  }

  return (
    <NavLink
      to={navItemHref(venueId, { label: card.label, path: card.targetPath, status: 'ready' })}
      className="flex flex-col gap-1 rounded-xl border border-slateish-200 bg-white px-4 py-3.5 text-left shadow-sm hover:bg-slateish-50"
    >
      <span className="flex items-center justify-between text-sm font-semibold text-ink-700">
        {card.label}
        <span aria-hidden="true">›</span>
      </span>
      <span className="text-xs text-slateish-600">{card.description}</span>
    </NavLink>
  )
}
